import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys, LIVE_REFETCH_MS } from "@/api/queryClient";
import type { Channel, ChannelSetup } from "@/types";

export function useChannels(opts?: { enabled?: boolean }): UseQueryResult<Channel[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: queryKeys.list("channels"),
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<Channel[]>("/channels");
    },
    // #299 (Review M-3): der Aufrufer darf den Abruf unterdruecken, wenn die Sicht
    // gar nicht freigegeben ist — sonst feuert die Sidebar auf JEDER Seite garantierte
    // 403er (drei pro Seitenaufruf, mit retry + refetchOnWindowFocus).
    enabled: !!session && (opts?.enabled ?? true),
    refetchInterval: LIVE_REFETCH_MS,
  });
}

export function useChannel(id: string | undefined): UseQueryResult<Channel> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.detail("channels", id) : ["resources", "channels", "_invalid"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<Channel>(`/channels/${id}`);
    },
    enabled: !!id && !!session,
    // Live-Refresh beim Pairing/Onboarding: solange noch keine gepairte externe
    // Identität gemeldet ist, alle 5s pollen. Ein online-Channel kann bereits
    // Inventory melden (pluginStatus "disconnected", externalIdentity null) und
    // trotzdem auf das Pairing warten — erst die externalIdentity beendet das Polling.
    refetchInterval: (query) => {
      const ch = query.state.data as Channel | undefined;
      if (ch?.inventorySnapshot?.externalIdentity) return false;
      return 5000;
    },
  });
}

// Channel-Setup-Flow: Setup-Zustand + opaques Plugin-Schema (SDUI). Poll statt SSE
// (Konzept §11.2): während des Setups 3s (QR→connected-Übergang), danach entspannt.
export function useChannelSetup(id: string | undefined): UseQueryResult<ChannelSetup> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.subResource("channels", id, "setup") : ["resources", "channels", "_invalid", "setup"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<ChannelSetup>(`/channels/${id}/setup`);
    },
    enabled: !!id && !!session,
    refetchInterval: (query) => {
      const setup = query.state.data as ChannelSetup | undefined;
      // Schnell nur während eines AKTIVEN Setups (needs_setup/in_progress/attention).
      // null (Plugin meldet kein Setup — Alt-Plugins) + ready → entspanntes Live-Intervall.
      const active = setup?.setupState && setup.setupState !== "ready";
      return active ? 3000 : LIVE_REFETCH_MS;
    },
  });
}
